import {
  AssetsResolverCSV,
  AssetsResolverV3CSV,
  entities,
} from '@api/api/entities';
import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { postMultipleEntities } from '@terminal/services/entities/entity-service';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';

export type FormattedResolvedEntityData = {
  tags: string[];
  duplicates: string[];
  entities: AllEntitiesResolved[];
};

export type FormattedResolvedEntityV3Data = {
  tags: {
    primaryTag: string;
    alternativeTags: string[];
  }[];
  duplicates: string[];
  entities: {
    primaryEntity: AllEntitiesResolved;
    alternativeEntities: AllEntitiesResolved[];
  }[];
};

export type EntityResolved = Entity & {
  input: string;
};

export type EntityNotResolved = Partial<Entity> & {
  input: string;
};

export type AllEntitiesResolved = EntityResolved | EntityNotResolved;

export const CHAT_INPUT_ENTITIES_REGEX = /[\n\r,]/;

/**
 * DEPRECATED
 */
export const resolveEntitiesFromCsvV2 = async (maybeCsv: string) => {
  const csv = maybeCsv.trim().split(CHAT_INPUT_ENTITIES_REGEX).join(',');
  const res = await apiFetch<AssetsResolverCSV>(
    entities.resolveFromCsvV2.path,
    {
      method: 'post',
      body: {
        csv,
      },
    }
  );
  const tags = res?.data?.assets.filter(r => r.tag).map(r => r.tag);
  const inputs = res?.data?.assets.map(r => r.input);

  const defaultReturn: FormattedResolvedEntityData = {
    tags: [],
    duplicates: [],
    entities: [],
  };

  if (res.error || !tags?.length) {
    return {
      ...defaultReturn,
      entities: inputs?.map(input => ({
        input,
      })),
    } as FormattedResolvedEntityData;
  }

  const entitiesData = await postMultipleEntities(tags);
  const result = res.data.assets.reduce((acc, resolved) => {
    const entity = entitiesData.find(e => e.tag === resolved.tag);

    if (entity) {
      if (!acc.tags.includes(entity.tag)) {
        acc.tags.push(entity.tag);
        acc.entities.push({
          ...entity,
          input: resolved.input,
        });
      } else {
        const ticker = formatTicker(entity);
        acc.duplicates.push(ticker);
      }
    } else {
      acc.entities.push({
        input: resolved.input,
      });
    }

    return acc;
  }, defaultReturn);

  return result;
};

const dedupeCsvV3Input = (maybeCsv: string) => {
  const preJoinedCsv = maybeCsv
    .trim()
    .split(CHAT_INPUT_ENTITIES_REGEX)
    .map(t => t.trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(preJoinedCsv)).join(',');
};

/** Tag stubs only — no postMultipleEntities; used before scroll hydration. */
export const resolveTagsFromCsvV3 = async (
  maybeCsv: string
): Promise<FormattedResolvedEntityV3Data> => {
  const dedupedCsvList = dedupeCsvV3Input(maybeCsv);
  const res = await apiFetch<AssetsResolverV3CSV>(
    entities.resolveFromCsvV3.path,
    {
      method: 'post',
      body: { csv: dedupedCsvList },
    }
  );

  const defaultReturn: FormattedResolvedEntityV3Data = {
    tags: [],
    duplicates: [],
    entities: [],
  };

  const inputs = res?.data?.assets.map(r => r.input) ?? [];
  const hasAnyTags = res?.data?.assets.some(r => r.tags?.length);

  if (res.error || !hasAnyTags) {
    return {
      ...defaultReturn,
      entities: inputs.map(input => ({
        primaryEntity: { input },
        alternativeEntities: [],
      })),
    };
  }

  const seenTags = new Set<string>();

  return res.data.assets.reduce<FormattedResolvedEntityV3Data>(
    (acc, resolved) => {
      const availableTags = resolved.tags || [];

      if (availableTags.length === 0) {
        acc.entities.push({
          primaryEntity: { input: resolved.input },
          alternativeEntities: [],
        });
        return acc;
      }

      const primaryTag = availableTags[0];
      const alternativeTags = availableTags.slice(1);

      availableTags.forEach(tag => {
        if (seenTags.has(tag)) {
          acc.duplicates.push(tag);
        } else {
          seenTags.add(tag);
        }
      });

      acc.tags.push({ primaryTag, alternativeTags });
      acc.entities.push({
        primaryEntity: { tag: primaryTag, input: resolved.input },
        alternativeEntities: alternativeTags.map(tag => ({
          tag,
          input: resolved.input,
        })),
      });

      return acc;
    },
    defaultReturn
  );
};

export const resolveEntitiesFromCsvV3 = async (maybeCsv: string) => {
  const dedupedCsvList = dedupeCsvV3Input(maybeCsv);
  const res = await apiFetch<AssetsResolverV3CSV>(
    entities.resolveFromCsvV3.path,
    {
      method: 'post',
      body: { csv: dedupedCsvList },
    }
  );
  const { tags, inputs } = res?.data?.assets.reduce<{
    tags: string[];
    inputs: string[];
  }>(
    (acc, r) => {
      if (r.tags?.length) {
        acc.tags.push(...r.tags);
      }

      acc.inputs.push(r.input);
      return acc;
    },
    { tags: [], inputs: [] }
  ) || { tags: [], inputs: [] };

  const defaultReturn: FormattedResolvedEntityV3Data = {
    tags: [],
    duplicates: [],
    entities: [],
  };

  if (res.error || !tags?.length) {
    return {
      ...defaultReturn,
      entities: (inputs ?? []).map(input => ({
        primaryEntity: { input },
        alternativeEntities: [],
      })),
    } as FormattedResolvedEntityV3Data;
  }

  const entitiesData = await postMultipleEntities(tags);
  const entityByTag = new Map<string, Entity>();
  entitiesData.forEach(e => entityByTag.set(e.tag, e));

  const seenTags = new Set<string>();

  const result = res.data.assets.reduce<FormattedResolvedEntityV3Data>(
    (acc, resolved) => {
      const availableTags = (resolved.tags || []).filter(tag =>
        entityByTag.has(tag)
      );

      if (availableTags.length === 0) {
        acc.entities.push({
          primaryEntity: { input: resolved.input },
          alternativeEntities: [],
        });
        return acc;
      }

      const primaryTag = availableTags[0];
      const alternativeTags = availableTags.slice(1);

      const primaryEntityBase = entityByTag.get(primaryTag);
      const primaryEntity: AllEntitiesResolved = primaryEntityBase
        ? { ...primaryEntityBase, input: resolved.input }
        : { input: resolved.input };

      const alternativeEntities: AllEntitiesResolved[] = alternativeTags.map(
        t => {
          const e = entityByTag.get(t);
          return e
            ? { ...e, input: resolved.input }
            : { input: resolved.input };
        }
      );

      acc.tags.push({ primaryTag, alternativeTags });

      availableTags.forEach(tag => {
        const e = entityByTag.get(tag);

        if (e) {
          if (seenTags.has(tag)) {
            acc.duplicates.push(formatTicker(e));
          } else {
            seenTags.add(tag);
          }
        }
      });

      acc.entities.push({
        primaryEntity,
        alternativeEntities,
      });

      return acc;
    },
    defaultReturn
  );

  return result;
};
